import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    type Injector,
    runInInjectionContext,
    signal,
    type Signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { MatDialogRef } from '@angular/material/dialog';
import { assertInjector, qpxToast as toast } from '@quipex/core/utils';
import { DialogService } from '@quipex/shared/ui';
import { take } from 'rxjs';
import {
    focusActivityDestination,
    shouldFocusActivityDestination,
} from '../activity-dialog/activity-dialog-result';
import type { ActivityKey } from '../activity-journal';
import type { ActivityToastView } from '../activity-runtime/action-contract';
import { injectActivity } from '../activity-runtime/context';
import {
    ActivityToastOwner,
    activityToastLifecycle,
} from '../activity-runtime/toast-owner';
import { PersistentActivityToastRegistry } from './persistent-toast-registry';
import {
    focusTopmostDialog,
    getFocusOrigin,
    handleToastActionResult,
    restoreActivityFocus,
    showActivityToast,
    type ActivityDialogCloseResult,
} from './presenter';

export interface ActivityLauncher {
    readonly attentionCount: Signal<number>;
    readonly hasInProgress: Signal<boolean>;
    readonly isOpen: Signal<boolean>;
    open(): Promise<void>;
}

export interface ActivityLauncherOptions {
    readonly injector?: Injector;
    readonly focusDestination?: () => void;
    readonly focusFallback?: () => void;
}

export function injectActivityLauncher(
    options: ActivityLauncherOptions = {}
): ActivityLauncher {
    const resolvedInjector = assertInjector(
        injectActivityLauncher,
        options.injector
    );
    const focusDestination = options.focusDestination ?? (() => undefined);
    const focusFallback = options.focusFallback ?? (() => undefined);
    const activity = runInInjectionContext(resolvedInjector, injectActivity);
    const destroyRef = resolvedInjector.get(DestroyRef);
    const dialogService = resolvedInjector.get(DialogService);
    const document = resolvedInjector.get(DOCUMENT);
    const toastOwner = resolvedInjector.get(ActivityToastOwner);
    const isOpenState = signal(false);
    const actionsInFlight = new Map<ActivityKey, symbol>();
    const persistentToasts = new PersistentActivityToastRegistry();
    let openDialogRef:
        | MatDialogRef<unknown, ActivityDialogCloseResult>
        | undefined;
    let openDialogIdentityVersion: number | undefined;
    const openingDialogs = new Map<number, Promise<void>>();
    let launchVersion = 0;
    let dialogVersion = 0;
    let destroyed = false;
    let observedIdentityVersion = activity.activeIdentityVersion();

    const open = async (): Promise<void> => {
        if (destroyed) return;

        const openingActivityIdentityVersion = activity.activeIdentityVersion();
        if (
            openDialogRef &&
            openDialogIdentityVersion === openingActivityIdentityVersion
        ) {
            return;
        }
        const existingOpeningDialog = openingDialogs.get(
            openingActivityIdentityVersion
        );
        if (existingOpeningDialog) {
            return await existingOpeningDialog;
        }

        const focusOrigin = getFocusOrigin(document);
        const openingLaunchVersion = launchVersion;
        const openingDialog = import('../activity-dialog/activity-dialog')
            .then(({ ActivityDialog }) => {
                if (
                    destroyed ||
                    openingLaunchVersion !== launchVersion ||
                    openingActivityIdentityVersion !==
                        activity.activeIdentityVersion()
                ) {
                    return undefined;
                }

                return runInInjectionContext(
                    resolvedInjector,
                    () =>
                        dialogService.openDialog(
                            {
                                component: ActivityDialog,
                            },
                            {
                                ariaLabel: 'Activity',
                                autoFocus: 'first-heading',
                                backdropClass: 'qpx-sidesheet-backdrop',
                                closeOnNavigation: false,
                                disableClose: false,
                                injector: resolvedInjector,
                                maxWidth: '100dvw',
                                panelClass: [
                                    'qpx-sidesheet',
                                    'flex-content',
                                    'no-padding',
                                    'no-scroll',
                                ],
                                position: {
                                    right: 'var(--sidesheet-viewport-margin)',
                                    top: 'var(--sidesheet-viewport-margin)',
                                },
                                restoreFocus: false,
                                width: '33rem',
                            }
                        ) as MatDialogRef<unknown, ActivityDialogCloseResult>
                );
            })
            .then((dialogRef): void => {
                if (!dialogRef) return;
                if (
                    destroyed ||
                    openingLaunchVersion !== launchVersion ||
                    openingActivityIdentityVersion !==
                        activity.activeIdentityVersion()
                ) {
                    dialogRef.close();
                    return;
                }

                openDialogRef = dialogRef;
                openDialogIdentityVersion = openingActivityIdentityVersion;
                dialogVersion += 1;
                isOpenState.set(true);
                dialogRef
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe((result) => {
                        if (openDialogRef === dialogRef) {
                            openDialogRef = undefined;
                            openDialogIdentityVersion = undefined;
                            dialogVersion += 1;
                            isOpenState.set(false);
                        }
                        if (destroyed) return;
                        if (focusTopmostDialog(document)) return;

                        if (shouldFocusActivityDestination(result)) {
                            document.defaultView?.setTimeout(() =>
                                focusActivityDestination(
                                    document,
                                    focusDestination
                                )
                            );
                        } else {
                            restoreActivityFocus(focusOrigin, focusFallback);
                        }
                    });
            })
            .catch(() => {
                if (
                    !destroyed &&
                    openingLaunchVersion === launchVersion &&
                    openingActivityIdentityVersion ===
                        activity.activeIdentityVersion()
                ) {
                    const toastId = 'activity:launch';
                    const toastScope = toastOwner.createScope(
                        () =>
                            openingActivityIdentityVersion ===
                            activity.activeIdentityVersion()
                    );
                    toast.error('Unable to open Activity. Try again.', {
                        id: toastId,
                        ...activityToastLifecycle(toastScope),
                    });
                    toastScope.own(toastId);
                }
            });
        openingDialogs.set(openingActivityIdentityVersion, openingDialog);

        try {
            await openingDialog;
        } finally {
            if (
                openingDialogs.get(openingActivityIdentityVersion) ===
                openingDialog
            ) {
                openingDialogs.delete(openingActivityIdentityVersion);
            }
        }
    };

    const executeToastAction = async (
        decision: ActivityToastView
    ): Promise<void> => {
        const action = decision.action;
        if (!action || actionsInFlight.has(decision.key)) return;

        const actionExecution = Symbol(decision.key);
        const actionIdentityVersion = activity.activeIdentityVersion();
        const actionScope = toastOwner.createScope(
            () => actionIdentityVersion === activity.activeIdentityVersion()
        );
        const actionDialogVersion = dialogVersion;
        const actionDialogRef = openDialogRef;
        let ownsFeedbackToast = false;
        actionsInFlight.set(decision.key, actionExecution);
        try {
            const result = await action.execute();
            const isActionCurrent = (): boolean =>
                !destroyed &&
                actionScope.isCurrent() &&
                actionIdentityVersion === activity.activeIdentityVersion() &&
                actionDialogVersion === dialogVersion &&
                actionDialogRef === openDialogRef;
            if (isActionCurrent()) {
                ownsFeedbackToast = handleToastActionResult(
                    decision.key,
                    result,
                    open,
                    openDialogRef,
                    document,
                    actionScope,
                    focusDestination
                );
            }
        } finally {
            if (!ownsFeedbackToast) actionScope.release();
            if (actionsInFlight.get(decision.key) === actionExecution) {
                actionsInFlight.delete(decision.key);
            }
        }
    };

    activity.toastDecisions$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((decision) => {
            persistentToasts.prepareReplacement(decision.key);
            const decisionIdentityVersion = activity.activeIdentityVersion();
            const toastScope = toastOwner.createScope(
                () =>
                    decisionIdentityVersion === activity.activeIdentityVersion()
            );
            const shown = showActivityToast(
                decision,
                open,
                executeToastAction,
                toastScope
            );
            persistentToasts.retain(decision.key, shown, toastScope);
        });

    runInInjectionContext(resolvedInjector, () =>
        toObservable(activity.activeIdentityVersion)
    )
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((version) => {
            if (observedIdentityVersion === version) return;
            observedIdentityVersion = version;
            actionsInFlight.clear();
            persistentToasts.destroyAll();
            if (openDialogIdentityVersion !== version) openDialogRef?.close();
        });

    destroyRef.onDestroy(() => {
        destroyed = true;
        launchVersion += 1;
        dialogVersion += 1;
        actionsInFlight.clear();
        persistentToasts.clear();
        openingDialogs.clear();
        toastOwner.invalidateAll();
        const dialogRef = openDialogRef;
        openDialogRef = undefined;
        openDialogIdentityVersion = undefined;
        isOpenState.set(false);
        dialogRef?.close();
    });

    return {
        attentionCount: activity.attentionCount,
        hasInProgress: activity.hasInProgress,
        isOpen: isOpenState.asReadonly(),
        open,
    };
}
